import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import ConfettiCannon from 'react-native-confetti-cannon';
import { BodyLargeRegular, FullScreenSpinner, YStack } from 'smoothie';

import FullScreenError from '../../../common/components/FullScreenError';
import { isEmpty } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException, captureMessage } from '../../../logger/Logger';
import useAcceptJointInvite from '../../hooks/useAcceptJointInvite';
import { KYCVerificationStatus } from '../../types/BankAccount';
import {
    getBankingDisclosures,
    HYS_ACCOUNT_OPEN_POLICY_DISCLOSURES,
} from '../Disclosures';

enum AcceptState {
    PROCESSING = 'processing',
    SUCCESS = 'success',
    ERROR = 'error',
}

const AcceptInviteLegacy = () => {
    LogRocketFacade.tagPage('AcceptInviteLegacy');

    const [acceptState, setAcceptState] = useState(AcceptState.PROCESSING);
    const accepted = useRef(false);

    const { userId, bankAccountId } = useLocalSearchParams<{
        userId: string;
        bankAccountId: string;
    }>();

    const user = useAuthedUser();

    const { mutate: acceptJointInvite } = useAcceptJointInvite();

    useEffect(() => {
        if (accepted.current) {
            return;
        }

        if (user.id !== userId) {
            captureException(new Error('User ID does not match'));
            router.navigate('/');
            return;
        }

        if (user.syncteraKycStatus !== KYCVerificationStatus.ACCEPTED) {
            // For savings accounts, simply pushing the user through KYC will eventually accept the account invite (which happens when handling the synctera webhook response)
            router.navigate('/banking/kyc-savings');
            return;
        }

        acceptJointInvite(
            {
                body: {
                    userId,
                    bankAccountId,
                    disclosures: getBankingDisclosures({
                        policyTypes: HYS_ACCOUNT_OPEN_POLICY_DISCLOSURES,
                    }),
                },
            },
            {
                onSuccess: () => {
                    setAcceptState(AcceptState.SUCCESS);
                    accepted.current = true;

                    setTimeout(() => {
                        router.navigate({
                            pathname: '/home/banking',
                        });
                    }, 2500);
                },
                onError: (e) => {
                    captureException(e);
                    setAcceptState(AcceptState.ERROR);
                },
            },
        );
    }, [user.syncteraKycStatus]);

    if (isEmpty(userId) && isEmpty(bankAccountId)) {
        captureMessage(
            'Missing userId and bankAccountId on accept joint invite page',
        );
        router.navigate('/');
    }

    if (acceptState === AcceptState.SUCCESS) {
        return (
            <YStack flex={1} justifyContent="center" alignItems="center">
                <BodyLargeRegular>Success!</BodyLargeRegular>
                <ConfettiCannon
                    count={200}
                    origin={{ x: 0, y: 0 }}
                    fadeOut={true}
                    fallSpeed={2000}
                />
            </YStack>
        );
    }

    if (acceptState === AcceptState.PROCESSING) {
        return <FullScreenSpinner />;
    }

    return <FullScreenError />;
};

export default AcceptInviteLegacy;
